import { useNavigate, useParams } from "react-router-dom";
import { PrimaryButton } from "../../components/buttons/PrimaryButton";
import { SecondaryButton } from "../../components/buttons/SecondaryButton";
import Form from "../../components/inputs/Form";
import Header from "../../components/layout/Header";
import { Page, PageBody, PageHeader } from "../../components/layout/Page";
import { addIcon, backArrow, save, trash } from "../../assets/icons";
import Card from "../../components/card/Card";
import "./device.css";
import { TextInput } from "../../components/inputs/TextInput";
import { useEffect, useState } from "react";
import { Data, SetState } from "../../lib/typeDeclarations";
import { pattern } from "../../lib/pattern";
import { createRecord, deleteRecord, updateRecord } from "../../server/fetchFunctions";
import { Autocomplete } from "../../components/inputs/Autocomplete";
import { useFindById, useListRecords } from "../../server/hooks/getData";
import { Loader } from "../../components/loader/Loader";
import { Dropdown } from "../../components/inputs/Dropdown";
import { Textarea } from "../../components/inputs/Textarea";
import { format } from "../../lib/inputFormatters";

export default function EditDevice() {
  document.title = "Eszköz szerkesztése";
  const navigate = useNavigate();
  const id = useParams().id || "";

  const { data: initDevice, loading: deviceL } = useFindById("devices", [], id);
  const { data: deviceFunc, loading: deviceFuncL } = useListRecords("deviceFunc", [], {});
  const { data: etalons, loading: etalonsL } = useListRecords("etalon", [], {});
  const { data: etalonFunc, loading: etalonFuncL } = useListRecords("etalonFunc", [], {});
  const { data: si_prefixes, loading: si_prefixesL } = useListRecords("siprefix", [], {});
  const { data: units, loading: unitsL } = useListRecords("units", [], {});
  const { data: paramList, loading: paramsL } = useListRecords("deviceParam", [], {
    device_id: id,
    orderBy: "device_function_sort_order",
    order: "ASC",
  });

  const [params, setParams] = useState(paramList.data || []);
  const [loading, setLoading] = useState(true);

  const [device, setDevice] = useState<Data>({
    type: "",
    description: "",
    description_id: "",
    manufacturer: "",
    manufacturer_id: "",
    cal_price: "",
    notes: "",
  });

  const [newDeviceParams, setNewDeviceParams] = useState<{ [key: string]: any }>({
    device_function_id: "",
    etalon_function_id: "",
    range: "",
    resolution: "",
    reference_value: "",
    si_prefix_id: "",
    unit_id: "",
    u_precentage: 0,
    u_digit: 0,
    u_value: 0,
    etalon_id: "",
    device_id: "",
  });

  const [sampleDevice, setSampleDevice] = useState<any>({ id: id });
  const { data: sampleParamList, loading: splL } = useListRecords("deviceParam", [sampleDevice], {
    device_id: sampleDevice.id,
    orderBy: "device_function_sort_order",
    order: "ASC",
  });

  const manufacturers = useListRecords("devManu", [device.manufacturer], {
    manufacturer: device.manufacturer,
    limit: 10,
  });
  const descriptions = useListRecords("devDesc", [device.description], { description: device.description, limit: 10 });
  const { data: deviceslist, loading: devListL } = useListRecords("devices", [], {});

  useEffect(() => {
    if (!deviceL && !paramsL) {
      setLoading(true);
      setDevice(initDevice);
      setParams(
        paramList.data.map((item) => ({
          ...item,
          range: Number(item.range),
          resolution: Number(item.resolution),
          reference_value: Number(item.reference_value),
          u_precentage: Number(item.u_precentage),
          u_digit: Number(item.u_digit),
          u_value: Number(item.u_value),
        })),
      );
      setLoading(false);
    }
  }, [initDevice, paramList]);

  //Functions
  const handleAutoClompleteManu = (item: Data, setValue: SetState) => {
    setValue((prev) => ({ ...prev, manufacturer: item.manufacturer, manufacturer_id: item.id }));
  };

  const handleAutoClompleteDesc = (item: Data, setValue: SetState) => {
    setValue((prev) => ({ ...prev, description: item.description, description_id: item.id }));
  };

  const updateDevice = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let newDevice = device;
    setLoading(true);
    if (!device.manufacturer_id) {
      const response = await createRecord("devManu", { manufacturer: device.manufacturer });
      newDevice = { ...newDevice, manufacturer_id: response.id };
    }

    if (!device.description_id) {
      const response = await createRecord("devDesc", { description: device.description });
      newDevice = { ...newDevice, description_id: response.id };
    }

    const response = await updateRecord("devices", id, newDevice);
    setLoading(false);
  };

  const handleParamsChange = async (e: any, item: any, property: string, num: boolean) => {
    const value = num ? format.toNumber(e.target.value) : e.target.value;
    let apiValue = value;
    if (value < 0 || value === "") apiValue = 0;
    setParams((prev) => prev.map((i) => (i.id === item.id ? { ...i, [property]: value } : i)));
    await updateRecord("deviceParam", item.id, { [property]: apiValue });
  };

  const addNewParam = async () => {
    const response = await createRecord("deviceParam", {
      ...newDeviceParams,
      device_id: id,
    });
    if (response) {
      setParams((prev) => [
        ...prev,
        {
          ...response,
          range: Number(response.range),
          resolution: Number(response.resolution),
          reference_value: Number(response.reference_value),
          u_precentage: Number(response.u_precentage),
          u_digit: Number(response.u_digit),
          u_value: Number(response.u_value),
        },
      ]);
      setNewDeviceParams({
        ...newDeviceParams,
        range: 0,
        resolution: 0,
        reference_value: 0,
        u_precentage: 0,
        u_digit: 0,
        u_value: 0,
        device_id: "",
      });
    }
  };

  const deleteParam = async (id: string) => {
    await deleteRecord("deviceParam", id);
    setParams((prev) => prev.filter((item) => item.id !== id));
  };

  const filterArray = (arr1: any[], arr2: any[], property: string) => {
    const set2 = new Set(arr2.map((item) => item[property]));
    return arr1.filter((item) => !set2.has(item[property]));
  };

  const populateSampleParams = async () => {
    setLoading(true);
    if (sampleParamList.data.length === 0) {
      setLoading(false);
      return;
    }

    await Promise.all(
      sampleParamList.data.map(async (item: any) => {
        delete item.id;
        item.device_id = id;
        return await createRecord("deviceParam", item);
      }),
    );
    window.location.reload();
    setLoading(false);
  };

  return (
    <Page>
      <Form onSubmit={(e) => updateDevice(e)}>
        <Header>
          <SecondaryButton btn={{ svgBefore: backArrow, text: "Vissza", onClick: () => navigate(-1) }} />
          <PrimaryButton btn={{ svgBefore: save, text: "Ment", type: "submit" }} />
        </Header>
        <PageHeader title="Eszköz szerkesztése" />
        {loading ? (
          <Loader />
        ) : (
          <PageBody>
            <div className="device">
              <Card title="Eszköz" style={{ width: "100%", maxWidth: 800 }}>
                <TextInput
                  stateKey="type"
                  inlineLabel="Típus"
                  state={device}
                  setState={setDevice}
                  pattern={pattern.normal_text}
                  labelWidth={170}
                  required
                />

                <Autocomplete
                  inlineLabel="Gyártó"
                  stateKey="manufacturer"
                  state={device}
                  setState={setDevice}
                  list={manufacturers.data.data}
                  listKeys={["manufacturer"]}
                  handleClick={handleAutoClompleteManu}
                  pattern={pattern.normal_text}
                  labelWidth={170}
                  required
                />

                <Autocomplete
                  inlineLabel="Megnevezés"
                  stateKey="description"
                  state={device}
                  setState={setDevice}
                  list={descriptions.data.data}
                  listKeys={["description"]}
                  handleClick={handleAutoClompleteDesc}
                  pattern={pattern.normal_text}
                  labelWidth={170}
                  required
                />
                <TextInput
                  stateKey="cal_price"
                  inlineLabel="Kalibrálási költség"
                  state={device}
                  setState={setDevice}
                  afterText="Ft"
                  pattern={pattern.normal_text}
                  labelWidth={170}
                />
                <Textarea state={device} setState={setDevice} stateKey="notes" label="Megjegyzések" />
              </Card>
              <Card style={{ width: "100%", justifyItems: "center" }}>
                <div>
                  <h2>Bizonyítvány sablon</h2>
                  <Dropdown
                    state={sampleDevice}
                    stateKey="type"
                    list={deviceslist.data}
                    listKeys={["type", "manufacturer"]}
                    listTarget="id"
                    placeholder="Eszköz másolása"
                    handleClick={(item: any) => {
                      setSampleDevice(item);
                    }}
                    setState={setSampleDevice}
                  />
                  <PrimaryButton
                    btn={{
                      text: "Másol",
                      onClick: populateSampleParams,
                      style: { width: "100%", marginTop: 10 },
                    }}
                  />
                </div>
              </Card>
              <div className="device_params">
                <Card style={{ width: "100%" }}>
                  <table>
                    <thead>
                      <tr>
                        <td>Mért paraméter</td>
                        <td>Tartomány</td>
                        <td>Referencia érték</td>
                        <td>Si-előtag</td>
                        <td>Mértékegység</td>
                        <td>Felbontás</td>
                        <td>U (%)</td>
                        <td>U (digit)</td>
                        <td>U (érték)</td>
                        <td>Etalon</td>
                        <td>Etalon funkció</td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <select
                            id="functions"
                            name="functions"
                            onChange={(e) =>
                              setNewDeviceParams({
                                ...newDeviceParams,
                                device_function_id: e.target.value,
                              })
                            }>
                            <option value="0">Válasszon...</option>
                            {!deviceFuncL &&
                              deviceFunc.data.map((item: any, index) => (
                                <option key={index} value={item.id}>
                                  {item.function}
                                </option>
                              ))}
                          </select>
                        </td>

                        <td>
                          <input
                            type="number"
                            value={newDeviceParams.range}
                            onChange={(e) =>
                              setNewDeviceParams({ ...newDeviceParams, range: format.toNumber(e.target.value) })
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={newDeviceParams.reference_value}
                            onChange={(e) =>
                              setNewDeviceParams({
                                ...newDeviceParams,
                                reference_value: format.toNumber(e.target.value),
                              })
                            }
                          />
                        </td>

                        <td>
                          <select
                            id="si_prefix"
                            name="si_prefix"
                            onChange={(e) =>
                              setNewDeviceParams({
                                ...newDeviceParams,
                                si_prefix_id: e.target.value,
                              })
                            }>
                            <option value="0">Válasszon...</option>
                            {!si_prefixesL &&
                              si_prefixes.data.map((item: any, index) => (
                                <option key={index} value={item.id}>
                                  {item.symbol}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td>
                          <select
                            id="units"
                            name="units"
                            onChange={(e) => setNewDeviceParams({ ...newDeviceParams, unit_id: e.target.value })}>
                            <option value="0">Válasszon...</option>
                            {!unitsL &&
                              units.data.map((item: any, index) => (
                                <option key={index} value={item.id}>
                                  {item.symbol}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            value={newDeviceParams.resolution}
                            onChange={(e) =>
                              setNewDeviceParams({ ...newDeviceParams, resolution: format.toNumber(e.target.value) })
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={newDeviceParams.u_precentage}
                            onChange={(e) =>
                              setNewDeviceParams({
                                ...newDeviceParams,
                                u_precentage: format.toNumber(e.target.value),
                              })
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={newDeviceParams.u_digit}
                            onChange={(e) =>
                              setNewDeviceParams({ ...newDeviceParams, u_digit: format.toNumber(e.target.value) })
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={newDeviceParams.u_value}
                            onChange={(e) =>
                              setNewDeviceParams({ ...newDeviceParams, u_value: format.toNumber(e.target.value) })
                            }
                          />
                        </td>
                        <td>
                          <select
                            id="etalons"
                            name="etalons"
                            onChange={(e) => setNewDeviceParams({ ...newDeviceParams, etalon_id: e.target.value })}>
                            <option value="0">Válasszon...</option>
                            {!etalonsL &&
                              etalons.data.map((item: any, index) => (
                                <option key={index} value={item.id}>
                                  {item.type}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td>
                          <select
                            id="functions"
                            name="functions"
                            onChange={(e) =>
                              setNewDeviceParams({
                                ...newDeviceParams,
                                etalon_function_id: e.target.value,
                              })
                            }>
                            <option value="0">Válasszon...</option>
                            {!etalonFuncL &&
                              etalonFunc.data.map((item: any, index) => (
                                <option key={index} value={item.id}>
                                  {item.function}
                                </option>
                              ))}
                          </select>
                        </td>
                        <td onClick={addNewParam} style={{ cursor: "pointer" }}>
                          <div
                            className="svg"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginLeft: 5,
                            }}>
                            {addIcon}
                          </div>
                        </td>
                      </tr>
                      {!paramsL &&
                        params
                          .sort((a: any, b: any) => {
                            if (a.device_function_sort_order === b.device_function_sort_order) {
                              return (
                                a.range * Number(si_prefixes.data.find((i) => i.id === a.si_prefix_id)?.factor || 1) -
                                b.range * Number(si_prefixes.data.find((i) => i.id === b.si_prefix_id)?.factor || 1)
                              );
                            }
                            return a.device_function_sort_order - b.device_function_sort_order;
                          })
                          .map((item: any, index) => (
                            <tr key={index}>
                              <td>
                                <select
                                  id="functions"
                                  name="functions"
                                  value={item.device_function_id}
                                  onChange={(e) => handleParamsChange(e, item, "device_function_id", false)}>
                                  <option value="0">Válasszon...</option>
                                  {!deviceFuncL &&
                                    deviceFunc.data.map((item: any, index) => (
                                      <option key={index} value={item.id}>
                                        {item.function}
                                      </option>
                                    ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={item.range}
                                  onChange={(e) => handleParamsChange(e, item, "range", true)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={item.reference_value}
                                  onChange={(e) => handleParamsChange(e, item, "reference_value", true)}
                                />
                              </td>

                              <td>
                                <select
                                  id="si_prefix"
                                  name="si_prefix"
                                  value={item.si_prefix_id}
                                  onChange={(e) => handleParamsChange(e, item, "si_prefix_id", false)}>
                                  <option value="0">Válasszon...</option>
                                  {!si_prefixesL &&
                                    si_prefixes.data.map((item: any, index) => (
                                      <option key={index} value={item.id}>
                                        {item.symbol}
                                      </option>
                                    ))}
                                </select>
                              </td>
                              <td>
                                <select
                                  id="units"
                                  name="units"
                                  value={item.unit_id}
                                  onChange={(e) => handleParamsChange(e, item, "unit_id", false)}>
                                  <option value="0">Válasszon...</option>
                                  {!unitsL &&
                                    units.data.map((item: any, index) => (
                                      <option key={index} value={item.id}>
                                        {item.symbol}
                                      </option>
                                    ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={item.resolution}
                                  onChange={(e) => handleParamsChange(e, item, "resolution", true)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={item.u_precentage}
                                  onChange={(e) => handleParamsChange(e, item, "u_precentage", true)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={item.u_digit}
                                  onChange={(e) => handleParamsChange(e, item, "u_digit", true)}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={item.u_value}
                                  onChange={(e) => handleParamsChange(e, item, "u_value", true)}
                                />
                              </td>
                              <td>
                                <select
                                  id="etalon"
                                  name="etalon"
                                  value={item.etalon_id}
                                  onChange={(e) => handleParamsChange(e, item, "etalon_id", false)}>
                                  <option value="0">Válasszon...</option>
                                  {!etalonsL &&
                                    etalons.data.map((item: any, index) => (
                                      <option key={index} value={item.id}>
                                        {item.type}
                                      </option>
                                    ))}
                                </select>
                              </td>
                              <td>
                                <select
                                  id="etalon_function"
                                  name="etalon_function"
                                  value={item.etalon_function_id}
                                  onChange={(e) => handleParamsChange(e, item, "etalon_function_id", false)}>
                                  <option value="0">Válasszon...</option>
                                  {!etalonFuncL &&
                                    etalonFunc.data.map((item: any, index) => (
                                      <option key={index} value={item.id}>
                                        {item.function}
                                      </option>
                                    ))}
                                </select>
                              </td>
                              <td
                                onClick={() => deleteParam(item.id)}
                                className="delete_row"
                                style={{ cursor: "pointer" }}>
                                <div
                                  className="svg"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginLeft: 5,
                                  }}>
                                  {trash}
                                </div>
                              </td>
                            </tr>
                          ))}
                    </tbody>
                  </table>
                </Card>
              </div>
            </div>
          </PageBody>
        )}
      </Form>
    </Page>
  );
}
